import React, { useEffect, useRef, useState } from 'react';

// Constellation
import { Box, IconButton } from '@zillow/constellation-10';
import { IconChevronLeftCircleFilled, IconChevronRightCircleFilled } from '@zillow/constellation-icons';

// Components
import Dots from './Dots';

// Hooks
import { useCarousel } from './hooks';

// Styles
import { nextStyles, prevStyles } from './styles';

// Types
type CarouselPropTypes = {
  width: number;
  items: Array<React.ReactElement>;
  autoScrollSpeed?: number;
  autoScrollPauseDuration?: number;
};

const TRANSITION_DURATION = 400; // css animation (milliseconds)

const Carousel = ({ width, items = [], autoScrollSpeed, autoScrollPauseDuration }: CarouselPropTypes) => {
  const count = items.length;
  // add items at start and end of carousel to simulate infinite scroll (see ./hooks)
  const extendedItems = [items[items.length - 1]].concat(items).concat(items[0]);

  const [canUseTransition, updateCanUseTransition] = useState(false);
  const {
    canAutoScroll,
    handleAutoScroll,
    handleGoToItem,
    handleNext,
    handlePrev,
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
    index,
    isInfiniteScrollComplete,
    translateX,
    updateTranslateX,
  } = useCarousel({
    autoScrollPauseDuration,
    autoScrollSpeed,
    count: items.length,
    transitionDuration: TRANSITION_DURATION,
    updateCanUseTransition,
    width,
  });

  // setInterval will reference state & props at time of declaration
  // useRef will use the current value of state/props
  const indexRef = useRef(index);
  const widthRef = useRef(width);
  const canAutoScrollRef = useRef(canAutoScroll);
  indexRef.current = index;
  widthRef.current = width;
  canAutoScrollRef.current = canAutoScroll;

  useEffect(() => {
    if (autoScrollSpeed) {
      const autoScrollInterval = setInterval(() => {
        if (canAutoScrollRef.current) {
          handleAutoScroll({
            index: indexRef.current,
            width: widthRef.current,
          });
        }
      }, autoScrollSpeed);
      return () => clearInterval(autoScrollInterval);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  useEffect(() => {
    updateTranslateX((index + 1) * width);

    setTimeout(() => {
      updateCanUseTransition(true);
    }, TRANSITION_DURATION);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [width]);

  const renderSkeletonCarousel = () => (
    <Box css={{ width: '100%', margin: '0 auto', padding: { base: '0 {spacing.600}', md: '0 100px' } }}>{items[0]}</Box>
  );

  const renderCarousel = () => (
    <>
      {extendedItems.map((item, idx) => (
        <Box key={idx} css={{ width: '100%', margin: '0 auto', padding: { base: '0 {spacing.600}', md: '0 100px' } }}>
          {item}
        </Box>
      ))}
    </>
  );

  return (
    <Box css={{ position: 'relative', width: '100%', overflow: 'hidden', padding: '{spacing.400} 0 {spacing.1200}' }}>
      <Box
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ transform: `translateX(-${translateX}px)` }}
        css={{
          // @ts-expect-error: -webkit-box is not a valid CSS property
          display: '-webkit-box',
          transition: canUseTransition && isInfiniteScrollComplete ? 'all 400ms ease-in-out' : 'none',
        }}
      >
        {width ? renderCarousel() : renderSkeletonCarousel()}
      </Box>
      <IconButton
        emphasis="bare"
        size="lg"
        tone="brand"
        title="Previous"
        icon={<IconChevronLeftCircleFilled />}
        css={prevStyles}
        onClick={handlePrev}
      />
      <IconButton
        emphasis="bare"
        size="lg"
        tone="brand"
        title="Next"
        icon={<IconChevronRightCircleFilled />}
        css={nextStyles}
        onClick={handleNext}
      />
      <Dots index={index} count={count} onGoToItem={handleGoToItem} />
    </Box>
  );
};

export default Carousel;
